import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "./index";
import { StoreStatus } from "./StoreStatus";

// Domain
import type { Client } from "../domain/ClientSlice/Client";
import type { ClientUpdateClientMe } from "../domain/ClientSlice/ClientUpdateClientMe";
// Removed repo update calls from store; updates will be performed at form level only
import { ClientMeRepository } from "../infrastructure/ClientSlice/ClientMeRepository";
import { initialStoreStateBase, StoreStateBase } from "./StoreStateBase";
import globalRoles from "../globalRoles";
import { doSecurityLogout, doSecurityRefreshToken } from "./useSecurityStore";

// State
export interface ClientState extends StoreStateBase {
  entity: Client | undefined;
  selectedAddressId: number | undefined;
}

const initialClientState: ClientState = {
  ...initialStoreStateBase,
  entity: undefined,
  selectedAddressId: undefined,
};

// Selectors
export const selectClientEntity = (s: RootState) => s.client.entity;
export const selectClientStatus = (s: RootState) => s.client?.storeStatus ?? StoreStatus.idle;

// Thunks
export const doClientFetchAuthenticated = createAsyncThunk<
  Client | null,
  void,
  { state: RootState }
>("client/fetchAuthenticated", async (_: void, { getState }) => {
  const s = getState();
  if (!s.security.isLoggedIn || !s.security.token?.accessToken || !s.security.roles?.includes(globalRoles.CLIENT)) return null;
  const fresh = await new ClientMeRepository().getOneAuthenticated();
  return fresh ?? null;
});

// Slice
export const clientSlice = createSlice({
  name: "client",
  initialState: initialClientState,
  reducers: {
    reset: (s) => {
      s.entity = undefined;
      s.storeStatus = StoreStatus.idle;
      s.error = null;
      s.selectedAddressId = undefined;
    },
    setClient(state, a: PayloadAction<Client | null>) {
      state.entity = a.payload ?? undefined;
    },
    setCreditBalance(state, a: PayloadAction<number>) {
      if (state.entity) {
        state.entity.creditBalance = a.payload;
      }
    },
    setSelectedAddressId(state, a: PayloadAction<number | null>) {
      state.selectedAddressId = a.payload ?? undefined;
    },
    clearClient(state) {
      state.entity = undefined;
      state.storeStatus = StoreStatus.idle;
      state.error = null;
      state.selectedAddressId = undefined;
    },
    // Update only the session store entity; do not call repositories here
    updateClientSessionStore(state, a: PayloadAction<ClientUpdateClientMe>) {
      // Do not sanitize country code: assume already sanitized by UI control
      state.entity = {
        ...state.entity,
        ...a.payload,
      } as Client;
    },
  },
  extraReducers: (b) => {
    b.addCase(doClientFetchAuthenticated.pending, (s) => {
      s.storeStatus = StoreStatus.loading;
    })
      .addCase(doClientFetchAuthenticated.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.entity = a.payload ?? undefined;
      })
      .addCase(doClientFetchAuthenticated.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Fetch client failed");
      })
      .addCase(doSecurityRefreshToken.rejected as any, (s) => {
        // Security slice resets; ensure client state is cleared too
        s.entity = undefined;
        s.storeStatus = StoreStatus.idle;
        s.error = null;
      })
      // Clear client session on security logout (e.g., session expired)
      .addCase(doSecurityLogout.fulfilled, (s) => {
        s.entity = undefined;
        s.storeStatus = StoreStatus.idle;
        s.error = null;
        s.selectedAddressId = undefined;
      });
  },
});

export const { setClient, clearClient, setCreditBalance, setSelectedAddressId, updateClientSessionStore } = clientSlice.actions;
export default clientSlice.reducer;
export const clientReducer = clientSlice.reducer;
  
// Facade hook
export function useClientStore() {
  const dispatch = useDispatch<AppDispatch>();
  const client = useSelector(selectClientEntity);
  const status = useSelector(selectClientStatus);
  const selectedAddressId = useSelector((s: RootState) => s.client.selectedAddressId);

  const clientFetchAuthenticatedAction = useCallback(
    () => dispatch(doClientFetchAuthenticated()),
    [dispatch]
  );
  // Pure store update; no repository calls here
  const updateClientSessionStoreAction = useCallback(
    (u: ClientUpdateClientMe) => dispatch(updateClientSessionStore(u)),
    [dispatch]
  );
  const setClientCredit = useCallback((value: number) => dispatch(setCreditBalance(value)), [dispatch]);
  const resetClientCredit = useCallback(() => dispatch(setCreditBalance(0)), [dispatch]);
  const setClientSelectedAddress = useCallback((id: number | null) => dispatch(setSelectedAddressId(id)), [dispatch]);

  return {
    client,
    clientId: client?.clientId ?? null,
    status,
    clientFetchAuthenticated: clientFetchAuthenticatedAction,
    updateClientSessionStore: updateClientSessionStoreAction,
    setClientCredit,
    resetClientCredit,
    selectedAddressId,
    setClientSelectedAddress,
  } as const;
}
